import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import * as actions from "../../../store/actions/index";
import { withRouter } from "../../../shared/utility";
import { canManageTenantSettings } from "../../../shared/tenantPermissions";
import { LocationDetailsViewModel } from "../../../viewmodels/setup/locations/LocationDetailsViewModel";
import SlideOver from "../../../components/slideOver/SlideOver";
import EmptyModal from "../../../components/modals/EmptyModal";
import AuditActionLink from "../../../components/audit/AuditActionLink";
import AuditHistoriesLink from "../../../components/audit/AuditHistoriesLink";
import Input from "../../../components/controls/Input";
import FormSectionLoadingSkeleton from "../../../components/operational/FormSectionLoadingSkeleton";
import SetupTabs from "../../../components/setup/SetupTabs";
import LocationResourcePanel from "../../../components/setup/location/LocationResourcePanel";
import { SetupActions, SetupTd } from "../../../components/setup/SetupPage";

const pageShellClass = "min-h-screen bg-[#F6F8FB] px-4 py-5 sm:px-6 lg:px-8";
const pageContentClass = "mx-auto max-w-5xl space-y-6";

const RESOURCE_TABS = {
  zones: "zones",
  rooms: "rooms",
};

function LocationsForbidden() {
  return (
    <div className="min-h-[60vh] bg-[#F6F8FB] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl rounded-[18px] border border-[#E2E8F0] bg-white px-6 py-10 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <h1 className="text-2xl font-semibold text-[#0F172A]">Access denied</h1>
        <p className="mt-3 text-sm leading-6 text-[#64748B]">
          Only company administrators can manage locations.
        </p>
      </div>
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={
        active
          ? "inline-flex rounded-full bg-[#DCFCE7] px-2.5 py-0.5 text-xs font-semibold text-[#166534]"
          : "inline-flex rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-semibold text-[#64748B]"
      }
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

const primaryButtonClass =
  "inline-flex h-10 items-center justify-center rounded-xl border border-[#16A34A] bg-[#16A34A] px-4 text-sm font-semibold text-white shadow-sm transition hover:border-[#15803D] hover:bg-[#15803D] disabled:opacity-50";

const dangerButtonClass =
  "inline-flex h-10 items-center justify-center rounded-xl border border-[#FECACA] bg-white px-4 text-sm font-semibold text-[#DC2626] shadow-sm transition hover:bg-[#FEF2F2] disabled:opacity-50";

function LocationDetails(props) {
  const {
    addSnackbar,
    logout,
    updateDefaultLocation,
    user_type,
    permissions,
    roles,
  } = props;
  const canManage = canManageTenantSettings(user_type, permissions, roles);
  const { locationId } = useParams();
  const navigate = useNavigate();
  const isNew = locationId === "new";

  const {
    emptyModal,
    setEmptyModelOpen,
    loading,
    saving,
    zonesLoading,
    roomsLoading,
    location,
    zones,
    rooms,
    getZones,
    getRooms,
    initLocation,
    createLocation,
    updateLocation,
    askDeleteLocation,
    slideOver,
    setSlideOver,
    openAddZoneSlideOver,
    openEditZoneSlideOver,
    askDeleteZone,
    openAddRoomSlideOver,
    openEditRoomSlideOver,
    askDeleteRoom,
  } = LocationDetailsViewModel(logout, addSnackbar, updateDefaultLocation);

  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [activeTab, setActiveTab] = useState(RESOURCE_TABS.zones);
  const [zoneFirst, setZoneFirst] = useState(0);
  const [zoneRows, setZoneRows] = useState(20);
  const [roomFirst, setRoomFirst] = useState(0);
  const [roomRows, setRoomRows] = useState(20);
  const zoneSearchRef = useRef();
  const roomSearchRef = useRef();

  useEffect(() => {
    if (!canManage || isNew) {
      return;
    }
    initLocation(locationId);
  }, [canManage, isNew, locationId]);

  useEffect(() => {
    if (location) {
      setName(location.name ?? "");
      setIsActive(location.is_active !== false && location.is_active !== 0);
      setIsDefault(location.is_default === 1 || location.is_default === true);
    }
  }, [location]);

  if (!canManage) {
    return <LocationsForbidden />;
  }

  const handleSave = async (e) => {
    e.preventDefault();
    const values = {
      name: name.trim(),
      is_active: isActive,
      is_default: isDefault,
    };

    if (!values.name) {
      return;
    }

    if (isNew) {
      const created = await createLocation(values);
      if (created?.id) {
        navigate(`/locations/${created.id}`);
      }
      return;
    }

    await updateLocation(values, location);
  };

  const handleZoneSearch = () => {
    if (!location) {
      return;
    }
    getZones(location, zoneSearchRef.current?.value ?? "", 1);
  };

  const handleZonePagination = (event) => {
    if (!location) {
      return;
    }
    const page = event.page + 1;
    setZoneFirst(event.first);
    setZoneRows(event.rows);
    getZones(location, zoneSearchRef.current?.value ?? "", page);
  };

  const handleRoomSearch = () => {
    if (!location) {
      return;
    }
    getRooms(location, roomSearchRef.current?.value ?? "", 1);
  };

  const handleRoomPagination = (event) => {
    if (!location) {
      return;
    }
    const page = event.page + 1;
    setRoomFirst(event.first);
    setRoomRows(event.rows);
    getRooms(location, roomSearchRef.current?.value ?? "", page);
  };

  const resourceTabs = [
    { id: RESOURCE_TABS.zones, label: `Zones (${zones.total || 0})` },
    {
      id: RESOURCE_TABS.rooms,
      label: `Production rooms (${rooms.total || 0})`,
    },
  ];

  if (!isNew && loading) {
    return (
      <div className={pageShellClass}>
        <div className={pageContentClass}>
          <div className="rounded-[18px] border border-[#E2E8F0] bg-white p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <FormSectionLoadingSkeleton rows={6} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageShellClass}>
      <div className={pageContentClass}>
        <div>
          <Link
            to="/locations"
            className="text-sm font-medium text-[#2563EB] hover:text-[#1D4ED8]"
          >
            ← Back to locations
          </Link>
        </div>

        <section className="overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="border-b border-[#E2E8F0] px-5 py-5 sm:px-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#64748B]">
              QA Trace / Master Data
            </p>
            <h1 className="mt-1 text-[1.65rem] font-semibold tracking-tight text-[#0F172A] sm:text-3xl">
              {isNew ? "New location" : (location?.name ?? "Location")}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[#64748B]">
              {isNew
                ? "Create a location, then add storage zones and production rooms."
                : "Update location details and manage zones and production rooms."}
            </p>
          </div>

          <form onSubmit={handleSave} className="space-y-5 px-5 py-6 sm:px-6">
            <Input
              label="Name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              labelOn={true}
              type="text"
              required
            />
            <label className="flex items-center gap-2 text-sm font-medium text-[#0F172A]">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-[#CBD5E1] text-[#16A34A] focus:ring-[#16A34A]"
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-[#0F172A]">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 rounded border-[#CBD5E1] text-[#16A34A] focus:ring-[#16A34A]"
              />
              Default location
            </label>

            <div className="flex flex-wrap gap-3 border-t border-[#E2E8F0] pt-5">
              <button
                type="submit"
                className={primaryButtonClass}
                disabled={saving || !name.trim()}
              >
                {saving
                  ? "Saving…"
                  : isNew
                    ? "Create location"
                    : "Save changes"}
              </button>
              {!isNew && location ? (
                <AuditActionLink
                  slug="location"
                  id={location.id}
                  name={location.name}
                />
              ) : null}
              {!isNew && location ? (
                <button
                  type="button"
                  className={dangerButtonClass}
                  disabled={saving}
                  onClick={() =>
                    askDeleteLocation(location, () => navigate("/locations"))
                  }
                >
                  Delete location
                </button>
              ) : null}
            </div>
          </form>
        </section>

        {!isNew && location ? (
          <section className="overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="border-b border-[#E2E8F0] px-5 py-5 sm:px-6">
              <h2 className="text-[1.65rem] font-semibold tracking-tight text-[#0F172A] sm:text-2xl">
                Zones &amp; rooms
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-[#64748B]">
                Manage storage zones for receiving and production rooms for
                manufactured products at this location.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <AuditHistoriesLink
                  slug={
                    activeTab === RESOURCE_TABS.zones
                      ? "location-zone"
                      : "location-production-room"
                  }
                />
              </div>
              <SetupTabs
                className="mt-5"
                tabs={resourceTabs}
                value={activeTab}
                onChange={setActiveTab}
              />
            </div>

            {activeTab === RESOURCE_TABS.zones ? (
              <LocationResourcePanel
                description="Storage zones within this location. Used when receiving individual items."
                searchPlaceholder="Search zones"
                searchRef={zoneSearchRef}
                onSearch={handleZoneSearch}
                addLabel="Add Zone"
                onAdd={openAddZoneSlideOver}
                loading={zonesLoading}
                columns={["Zone", "Status"]}
                emptyText="No zones yet. Add a zone to use this location when receiving."
                first={zoneFirst}
                rows={zoneRows}
                totalRecords={zones.total}
                onPageChange={handleZonePagination}
              >
                {zones.data?.map((zone) => (
                  <tr
                    key={zone.id}
                    className="transition hover:bg-[#F6F8FB]/80"
                  >
                    <SetupTd muted={false}>{zone.name}</SetupTd>
                    <SetupTd muted={true}>
                      <StatusBadge active={zone.is_active !== false} />
                    </SetupTd>
                    <SetupActions>
                      <AuditActionLink
                        slug="location-zone"
                        id={zone.id}
                        name={zone.name}
                      />
                      <button
                        type="button"
                        onClick={() => openEditZoneSlideOver(zone)}
                        className="inline-flex min-w-[68px] justify-center rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-sm font-semibold text-[#0F172A] shadow-sm transition hover:border-[#2563EB] hover:bg-[#EFF6FF] hover:text-[#2563EB]"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => askDeleteZone(zone)}
                        className="ml-2 inline-flex min-w-[68px] justify-center rounded-lg border border-[#FECACA] bg-white px-3 py-1.5 text-sm font-semibold text-[#DC2626] shadow-sm transition hover:border-[#FCA5A5] hover:bg-[#FEF2F2]"
                      >
                        Delete
                      </button>
                    </SetupActions>
                  </tr>
                ))}
              </LocationResourcePanel>
            ) : (
              <LocationResourcePanel
                description="Production rooms within this location. Used when recording manufactured products."
                searchPlaceholder="Search rooms"
                searchRef={roomSearchRef}
                onSearch={handleRoomSearch}
                addLabel="Add Room"
                onAdd={openAddRoomSlideOver}
                loading={roomsLoading}
                columns={["Room", "Status"]}
                emptyText="No rooms yet. Add a production room for this location."
                first={roomFirst}
                rows={roomRows}
                totalRecords={rooms.total}
                onPageChange={handleRoomPagination}
              >
                {rooms.data?.map((room) => (
                  <tr
                    key={room.id}
                    className="transition hover:bg-[#F6F8FB]/80"
                  >
                    <SetupTd muted={false}>{room.name}</SetupTd>
                    <SetupTd muted={true}>
                      <StatusBadge
                        active={
                          room.is_active !== false && room.is_active !== 0
                        }
                      />
                    </SetupTd>
                    <SetupActions>
                      <AuditActionLink
                        slug="location-production-room"
                        id={room.id}
                        name={room.name}
                      />
                      <button
                        type="button"
                        onClick={() => openEditRoomSlideOver(room)}
                        className="inline-flex min-w-[68px] justify-center rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-sm font-semibold text-[#0F172A] shadow-sm transition hover:border-[#2563EB] hover:bg-[#EFF6FF] hover:text-[#2563EB]"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => askDeleteRoom(room)}
                        className="ml-2 inline-flex min-w-[68px] justify-center rounded-lg border border-[#FECACA] bg-white px-3 py-1.5 text-sm font-semibold text-[#DC2626] shadow-sm transition hover:border-[#FCA5A5] hover:bg-[#FEF2F2]"
                      >
                        Delete
                      </button>
                    </SetupActions>
                  </tr>
                ))}
              </LocationResourcePanel>
            )}
          </section>
        ) : null}

        <EmptyModal
          childComponent={emptyModal.childComponent}
          open={emptyModal.open}
          setOpen={setEmptyModelOpen}
        />
        <SlideOver
          childComponent={slideOver.childComponent}
          slideOver={slideOver}
          setSlideOver={setSlideOver}
        />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  user_type: state.auth.user_type,
  permissions: state.auth.permissions,
  roles: state.auth.roles,
});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  updateDefaultLocation: (defaultLocation) =>
    dispatch(actions.updateDefaultLocationSuccess(defaultLocation)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LocationDetails),
);
